/*
 * Copyright (c) 2016 Macrofocus GmbH. All Rights Reserved.
 */
package com.macrofocus.docking.js

import com.macrofocus.common.command.Command
import com.macrofocus.common.properties.SimpleArrayProperty
import com.macrofocus.docking.Dockable
import com.macrofocus.docking.DockingAnchor
import com.macrofocus.docking.DockingBar
import com.macrofocus.docking.DockingState
import com.macrofocus.docking.DockingType
import js.objects.unsafeJso
import kotlinx.browser.document
import org.mkui.component.CPComponent
import org.mkui.component.ReactComponent
import org.mkui.react.useProperty
import react.FC
import react.Props
import react.create
import react.dom.client.Root
import react.dom.client.createRoot
import react.dom.html.ReactHTML.div
import web.cssom.*
import web.dom.Element

/**
 * React counterpart of the Swing `SwingDockingBar`: a row (north/south) or column (west/east) of tabs, aligned to
 * the start and only as large as its tabs. Activating a tab shows its dockable in the associated [DockingContainer].
 */
class JsDockingBar(private val container: DockingContainer, private val orientation: DockingAnchor) : DockingBar<CPComponent> {
    private val components = SimpleArrayProperty<CPComponent>(arrayOf())

    override val nativeComponent: CPComponent = ReactComponent(FC<Props> {
        val (buttons, _) = useProperty(components)
        val sideBar = orientation == DockingAnchor.LEFT || orientation == DockingAnchor.RIGHT

        div {
            className = ClassName("buttonArea")
            asDynamic()["data-testid"] = "DockingBar:$orientation"

            style = unsafeJso {
                position = Position.relative
                display = Display.flex
                flexDirection = if (sideBar) FlexDirection.column else FlexDirection.row
                alignItems = AlignItems.flexStart
                alignSelf = AlignSelf.flexStart
                justifySelf = JustifySelf.flexStart
                pointerEvents = Auto.auto
                minWidth = 0.px
                minHeight = 0.px
            }

            for (c in buttons) {
                c.nativeComponent()
            }
        }
    })

    override val dockables: MutableList<Dockable<CPComponent>?> = ArrayList()
    private val dockingButtons: MutableMap<Dockable<CPComponent>, DockingButton> = HashMap()
    private val dockingStates: MutableMap<Dockable<CPComponent>, DockingState> = HashMap()
    private val floatingWindows: MutableMap<Dockable<CPComponent>, Pair<Element, Root>> = HashMap()

    override fun attach(dockable: Dockable<CPComponent>) {
        dockable.dockingBar = this

        val first = dockables.isEmpty()
        dockables.add(dockable)

        val button = DockingButton(orientation)
        button.text = dockable.shortTitle
        button.command = Command { setActive(dockable, !button.selected) }
        button.onRightClick = Command { setFloating(dockable, true) }
        dockingButtons[dockable] = button
        dockingStates[dockable] = DockingState()

        updateButtons()

        if (first) {
            setActive(dockable, true)
        }
    }

    override fun detach(dockable: Dockable<CPComponent>) {
        setFloating(dockable, false)
        setActive(dockable, false)
        dockables.remove(dockable)
        dockingButtons.remove(dockable)
        dockingStates.remove(dockable)
        updateButtons()
    }

    override fun removeAll() {
        for (dockable in dockables.filterNotNull()) {
            setFloating(dockable, false)
            setActive(dockable, false)
        }
        dockingStates.clear()
        dockingButtons.clear()
        dockables.clear()
        container.visible = false
        updateButtons()
    }

    private fun updateButtons() {
        components.value = dockables.mapNotNull { dockingButtons[it] }.toTypedArray<CPComponent>()
    }

    override fun isActive(dockable: Dockable<CPComponent>): Boolean {
        return dockingStates[dockable]!!.isActive
    }

    override fun setActive(dockable: Dockable<CPComponent>, active: Boolean) {
        val state = dockingStates[dockable]!!

        if (!state.isFloating) {
            for (d in dockables) {
                val s = dockingStates[d]!!
                if (s.isActive) {
                    s.isActive = d === dockable && active
                    dockingButtons[d]!!.selected = d === dockable && active
                    container.remove(d!!.component)
                }
            }
            if (active) {
                state.isActive = true
                container.center = dockable.component
                container.visible = true
                dockingButtons[dockable]!!.selected = true
            } else {
                container.visible = false
            }
        } else {
            dockingButtons[dockable]!!.selected = false
        }
    }

    override fun setFloating(dockable: Dockable<CPComponent>, floating: Boolean) {
        val state = dockingStates[dockable] ?: return

        if (floating) {
            if (!state.isFloating) {
                setActive(dockable, false)
                val component = dockable.detachContainer()
                state.setType(DockingType.FLOATING)
                if (component != null) {
                    floatingWindows[dockable] = openFloatingWindow(dockable, component)
                }
            }
        } else {
            if (state.isFloating) {
                floatingWindows.remove(dockable)?.let { (element, root) ->
                    root.unmount()
                    element.remove()
                }
                dockable.attachContainer()
                state.setType(DockingType.DOCKED)
                setActive(dockable, true)
            }
        }
    }

    /** Browsers have no secondary windows, so floating dockables are shown in an overlay. */
    private fun openFloatingWindow(dockable: Dockable<CPComponent>, component: CPComponent): Pair<Element, Root> {
        val overlay = document.createElement("div")
        overlay.setAttribute(
            "style",
            "position:fixed;top:100px;left:200px;width:400px;height:300px;z-index:10000;display:flex;flex-direction:column;" +
                "background:white;border:1px solid #8e8e8e;box-shadow:0 2px 8px rgba(0,0,0,0.3);font-size:12px"
        )
        document.body?.appendChild(overlay)
        val element = overlay.unsafeCast<Element>()
        val root = createRoot(element)
        root.render(FC<Props> {
            div {
                style = unsafeJso {
                    display = Display.flex
                    alignItems = AlignItems.center
                    height = 22.px
                    padding = Padding(0.px, 5.px)
                    borderBottom = Border(1.px, LineStyle.solid, rgb(142, 142, 142))
                    backgroundImage = "linear-gradient(rgb(248, 248, 248), rgb(227, 227, 227))".unsafeCast<BackgroundImage>()
                }
                div {
                    style = unsafeJso { flexGrow = number(1.0) }
                    +(dockable.title ?: dockable.shortTitle ?: "")
                }
                div {
                    title = "Dock"
                    style = unsafeJso { cursor = Cursor.pointer }
                    onClick = { setFloating(dockable, false) }
                    +"×"
                }
            }
            div {
                style = unsafeJso {
                    position = Position.relative
                    flexGrow = number(1.0)
                    minHeight = 0.px
                }
                component.nativeComponent()
            }
        }.create())
        return Pair(element, root)
    }

    override fun setSliding(dockable: Dockable<CPComponent>, sliding: Boolean) {
        val state = dockingStates[dockable] ?: return

        if (sliding) {
            state.setType(DockingType.SLIDING)
            dockingButtons[dockable]?.selected = false
        } else {
            state.setType(DockingType.DOCKED)
            setActive(dockable, true)
        }
    }

    override fun dispose() {
        for ((element, root) in floatingWindows.values) {
            root.unmount()
            element.remove()
        }
        floatingWindows.clear()
        dockingStates.clear()
        dockingButtons.clear()
    }
}
